/**
 * Seed Calculator Logic
 * Extracted from sections/seed-calculator.liquid
 *
 * Requires window.seedCalculatorConfig to be set with:
 * - products: Array of product data from Shopify
 * - badgeLabels: Comma-separated badge labels
 * - badgeTags: Comma-separated badge tags
 */

(function() {
  // Get configuration from Liquid-injected data
  const config = window.seedCalculatorConfig || {};
  const shopifyProducts = config.products || [];

  // Debug: Log loaded products
  console.log('=== SHOPIFY PRODUCTS LOADED ===');
  console.log('Total products:', shopifyProducts.length);
  shopifyProducts.forEach((p, i) => {
    console.log(`Product ${i + 1}:`, p.title, 'Zones:', p.zones);
  });

  // Parse badge configurations from comma-separated settings
  const productBadges = (() => {
    const labelsStr = config.badgeLabels || '';
    const tagsStr = config.badgeTags || '';

    if (!labelsStr || !tagsStr) return [];

    const labels = labelsStr.split(',').map(s => s.trim()).filter(s => s);
    const tags = tagsStr.split(',').map(s => s.trim()).filter(s => s);

    // Match labels with tags by index
    const badges = [];
    const minLength = Math.min(labels.length, tags.length);

    for (let i = 0; i < minLength; i++) {
      badges.push({
        label: labels[i],
        tag: tags[i]
      });
    }

    return badges;
  })();

  console.log('Product badges configured:', productBadges);

  let detectedRegion = null;
  let zipEntered = false;
  let userZipCode = null;
  let usdaHardinessZone = null;
  let usdaTempRange = null;

  // Function to look up USDA hardiness zone by ZIP code
  function getUSDAZone(zipCode) {
    return window.usdaZonesByZip && window.usdaZonesByZip[zipCode] ? window.usdaZonesByZip[zipCode] : null;
  }

  // Function to look up climate region by ZIP code
  function getClimateRegionByZip(zipCode) {
    return window.zipToClimateRegion && window.zipToClimateRegion[zipCode] ? window.zipToClimateRegion[zipCode] : null;
  }

  // Function to detect climate region based on coordinates
  function detectClimateRegion(lat, lng) {
    if (lat > 44 && lng < -92) return "1w";
    if (lat > 42 && lng > -80) return "1e";
    if (lat > 38 && lat <= 44 && lng < -92) return "2w";
    if (lat > 37 && lat <= 42 && lng >= -92 && lng < -75) return "2e";
    if (lat > 33 && lat <= 38 && lng < -92) return "3w";
    if (lat > 32 && lat <= 37 && lng >= -92 && lng < -75) return "3e";
    if (lat <= 33 && lng >= -100 && lng < -80) return "4";
    if (lat > 37 && lng >= -116 && lng < -100) return "5";
    if (lat <= 37 && lng >= -116 && lng < -100) return "6";
    if (lng < -116) return "7";
    return "2e";
  }

  // Function to collect selected tags from questionnaire
  function getSelectedTags() {
    const selectedTags = [];
    const questionnaireInputs = document.querySelectorAll('.seed-calc-question-options input:checked');

    questionnaireInputs.forEach(input => {
      // Skip empty values (the "Any" option)
      if (input.value && input.value.trim() && input.value.trim() !== '') {
        selectedTags.push(input.value.trim());
      }
    });

    return selectedTags;
  }

  // Function to get recommended products for a region
  function getRecommendedProducts(region, selectedTags = []) {
    console.log('Looking for products in region:', region);
    console.log('Selected tags:', selectedTags);
    console.log('Available products:', shopifyProducts);

    // Make comparison case-insensitive and flexible
    const normalizedRegion = region.toLowerCase().trim();
    const normalizedSelectedTags = selectedTags.map(tag => tag.toLowerCase().trim());

    const filtered = shopifyProducts.filter(product => {
      console.log('Product:', product.title, 'Zones:', product.zones, 'Tags:', product.tags);

      // Check if any of the product's zones match the detected region
      const matchesZone = product.zones.some(zone => {
        const normalizedZone = zone.toLowerCase().trim();
        // Match exact zone code (e.g., "1w") or if zone contains it (e.g., "Climate Region 1W")
        return normalizedZone === normalizedRegion || normalizedZone.includes(normalizedRegion);
      });

      // If no tags selected, only filter by zone
      if (normalizedSelectedTags.length === 0) {
        return matchesZone;
      }

      // Check if product has ALL selected tags (must match all selected tags)
      const productTagsNormalized = product.tags.map(tag => tag.toLowerCase().trim());
      const matchesTags = normalizedSelectedTags.every(selectedTag =>
        productTagsNormalized.includes(selectedTag)
      );

      return matchesZone && matchesTags;
    });

    console.log('Filtered products:', filtered);
    return filtered;
  }

  // Helper function to extract weight from variant title
  function getVariantWeight(variant) {
    const weightMatch = variant.title.match(/(\d+(?:\.\d+)?|\d+\/\d+)\s*lb/i);
    if (!weightMatch) return 0;

    const weightStr = weightMatch[1];
    if (weightStr.includes('/')) {
      const parts = weightStr.split('/');
      return parseFloat(parts[0]) / parseFloat(parts[1]);
    }
    return parseFloat(weightStr);
  }

  // Function to calculate coverage rate
  function getCoverageRate(variant) {
    if (!variant.coverage || variant.coverage === 0) return 0;
    const weight = getVariantWeight(variant);
    if (weight === 0) return 0;
    return variant.coverage / weight;
  }

  // Function to calculate amount needed for a product based on area
  function calculateLbsNeeded(product, sqft) {
    const referenceVariant = product.variants.find(v => v.coverage > 0);
    if (!referenceVariant) return 0;

    const coverageRate = getCoverageRate(referenceVariant);
    return coverageRate > 0 ? sqft / coverageRate : 0;
  }

  // Helper function to calculate total cost of a combination
  function calculateCombinationCost(combination) {
    return combination.reduce((total, item) => total + (item.variant.price * item.quantity), 0);
  }

  // Helper function to calculate total weight of a combination
  function calculateCombinationWeight(combination) {
    return combination.reduce((total, item) => total + (item.variant.weight * item.quantity), 0);
  }

  // Function to find best variant combination for a product (same as single calculator)
  function findBestVariantCombination(product, lbsNeeded) {
    const validVariants = product.variants.filter(v => v.available && v.coverage > 0);
    if (validVariants.length === 0) return null;

    const variantsWithWeight = validVariants.map(variant => ({
      ...variant,
      weight: getVariantWeight(variant)
    })).sort((a, b) => b.weight - a.weight); // Sort largest to smallest

    // Greedy algorithm: Fill from largest to smallest
    let remaining = lbsNeeded;
    const combination = [];

    for (let i = 0; i < variantsWithWeight.length; i++) {
      const variant = variantsWithWeight[i];
      if (remaining <= 0) break;

      const quantity = Math.floor(remaining / variant.weight);
      if (quantity > 0) {
        combination.push({ variant, quantity });
        remaining -= quantity * variant.weight;
      }
    }

    // If we still need more, add one more of the smallest size
    if (remaining > 0 && variantsWithWeight.length > 0) {
      const smallestVariant = variantsWithWeight[variantsWithWeight.length - 1];
      const existingEntry = combination.find(item => item.variant.id === smallestVariant.id);

      if (existingEntry) {
        existingEntry.quantity += 1;
      } else {
        combination.push({ variant: smallestVariant, quantity: 1 });
      }
    }

    // Consolidation step: Replace multiple small bags with fewer large bags
    for (let i = variantsWithWeight.length - 1; i > 0; i--) {
      const smallVariant = variantsWithWeight[i];
      const smallEntry = combination.find(item => item.variant.id === smallVariant.id);

      if (!smallEntry || smallEntry.quantity < 2) continue;

      for (let j = i - 1; j >= 0; j--) {
        const largeVariant = variantsWithWeight[j];
        const totalSmallWeight = smallEntry.quantity * smallVariant.weight;
        const largeEquivalent = totalSmallWeight / largeVariant.weight;

        if (Number.isInteger(largeEquivalent) || Math.floor(largeEquivalent) > 0) {
          const largeBagsToUse = Math.floor(largeEquivalent);
          const smallBagsRemaining = Math.round((totalSmallWeight - (largeBagsToUse * largeVariant.weight)) / smallVariant.weight);

          if (largeBagsToUse > 0 && (largeBagsToUse + smallBagsRemaining) < smallEntry.quantity) {
            const largeEntry = combination.find(item => item.variant.id === largeVariant.id);

            if (largeEntry) {
              largeEntry.quantity += largeBagsToUse;
            } else {
              combination.push({ variant: largeVariant, quantity: largeBagsToUse });
            }

            if (smallBagsRemaining > 0) {
              smallEntry.quantity = smallBagsRemaining;
            } else {
              combination.splice(combination.indexOf(smallEntry), 1);
            }
            break;
          }
        }
      }
    }

    // Re-sort combination by weight (largest first) for display
    combination.sort((a, b) => b.variant.weight - a.variant.weight);

    if (combination.length === 0) return null;

    // Cost optimization: Check if rounding up to simpler options saves money
    const currentCost = calculateCombinationCost(combination);
    let bestCombination = combination;
    let bestCost = currentCost;

    // Try single bags that meet or exceed the requirement
    for (const variant of variantsWithWeight) {
      if (variant.weight >= lbsNeeded) {
        const singleBagCost = variant.price;
        if (singleBagCost < bestCost) {
          bestCost = singleBagCost;
          bestCombination = [{ variant, quantity: 1 }];
        }
      }
    }

    // Try rounding up to multiples of larger bags
    for (const variant of variantsWithWeight) {
      const qtyNeeded = Math.ceil(lbsNeeded / variant.weight);
      const totalCost = variant.price * qtyNeeded;

      if (totalCost < bestCost) {
        bestCost = totalCost;
        bestCombination = [{ variant, quantity: qtyNeeded }];
      }
    }

    return bestCombination;
  }

  // Generate badge HTML for matching product tags only
  function renderProductBadges(product) {
    if (!product.tags || product.tags.length === 0 || productBadges.length === 0) return '';

    const productTagsLower = product.tags.map(tag => tag.toLowerCase().trim());
    const matchingBadges = [];

    productBadges.forEach((badge) => {
      if (productTagsLower.includes(badge.tag.toLowerCase().trim())) {
        matchingBadges.push({
          label: badge.label
        });
      }
    });

    if (matchingBadges.length === 0) return '';

    const badgeHTML = matchingBadges.map(badge =>
      `<span class="seed-calc-product-badge">${badge.label}</span>`
    ).join('');

    return `<div class="seed-calc-product-badges">${badgeHTML}</div>`;
  }

  function updateResultsFromSqft(sqft) {
    console.log('=== UPDATE RESULTS ===');
    console.log('Square feet:', sqft);
    console.log('Detected region:', detectedRegion);

    const regionNames = {
      '1w': 'Region 1W - Cold & Humid',
      '1e': 'Region 1E - Cold & Humid',
      '2w': 'Region 2W - Cool & Humid',
      '2e': 'Region 2E - Cool & Humid',
      '3w': 'Region 3W - Temperate & Humid',
      '3e': 'Region 3E - Temperate & Humid',
      '4': 'Region 4 - Hot & Humid',
      '5': 'Region 5 - Cool & Dry',
      '6': 'Region 6 - Hot & Dry',
      '7': 'Region 7 - Temperate & Mixed'
    };

    let resultsHTML = '<div class="seed-calc-result-table">';

    resultsHTML += `
      <div class="seed-calc-result-row">
        <span class="seed-calc-result-label">Total Area:</span>
        <span class="seed-calc-result-value">${Math.round(sqft).toLocaleString()} sq ft</span>
      </div>
    `;

    if (detectedRegion) {
      resultsHTML += `
        <div class="seed-calc-result-row">
          <span class="seed-calc-result-label">Climate Region:</span>
          <span class="seed-calc-result-value">${regionNames[detectedRegion] || detectedRegion}</span>
        </div>
      `;
    }

    if (usdaHardinessZone) {
      let zoneDisplay = usdaHardinessZone;
      if (usdaTempRange) {
        zoneDisplay += ` (${usdaTempRange}°F)`;
      }
      resultsHTML += `
        <div class="seed-calc-result-row">
          <span class="seed-calc-result-label">USDA Hardiness Zone:</span>
          <span class="seed-calc-result-value">${zoneDisplay}</span>
        </div>
      `;
    }

    resultsHTML += '</div>';

    const selectedTags = getSelectedTags();
    const recommendedProducts = getRecommendedProducts(detectedRegion, selectedTags);

    if (recommendedProducts.length > 0) {
      resultsHTML += '<div class="seed-calc-product-grid">';

      let displayedProductCount = 0;

      recommendedProducts.forEach((product, index) => {
        console.log('=== Processing product:', product.title);
        console.log('Product variants:', product.variants);

        const lbsNeeded = calculateLbsNeeded(product, sqft);
        console.log('Lbs needed:', lbsNeeded);

        displayedProductCount++;

        // Check if product has coverage data
        if (lbsNeeded === 0) {
          console.warn('No coverage data for', product.title, '- showing product without calculator');

          // Show product without coverage calculator (just title and image linked)
          resultsHTML += `
            <div class="seed-calc-product-card seed-calc-product-card-no-coverage">
              <a href="${product.url}" class="seed-calc-product-link">
                <img src="${product.image}" alt="${product.title}" class="seed-calc-product-image">
              </a>
              <div class="seed-calc-product-details">
                <a href="${product.url}" class="seed-calc-product-link">
                  <h4 class="seed-calc-product-title">${product.title}</h4>
                </a>
                ${renderProductBadges(product)}
                <p class="seed-calc-no-coverage-note">Click to view product details</p>
              </div>
            </div>
          `;
          return;
        }

        const combination = findBestVariantCombination(product, lbsNeeded);
        console.log('Variant combination:', combination);

        if (!combination) {
          console.warn('No valid combination for', product.title);

          // Show product without add to cart (no available variants)
          resultsHTML += `
            <div class="seed-calc-product-card seed-calc-product-card-no-coverage">
              <a href="${product.url}" class="seed-calc-product-link">
                <img src="${product.image}" alt="${product.title}" class="seed-calc-product-image">
              </a>
              <div class="seed-calc-product-details">
                <a href="${product.url}" class="seed-calc-product-link">
                  <h4 class="seed-calc-product-title">${product.title}</h4>
                </a>
                ${renderProductBadges(product)}
                <p class="seed-calc-no-coverage-note">Currently unavailable - Click to view details</p>
              </div>
            </div>
          `;
          return;
        }

        // Build variant table
        const variantTableRows = combination.map(item => `
          <div class="variant-table-row">
            <span class="variant-size">${item.variant.title}</span>
            <span class="variant-quantity">Qty: ${item.quantity}</span>
          </div>
        `).join('');

        // Build cart items JSON
        const cartItems = JSON.stringify(combination.map(item => ({
          id: item.variant.id,
          quantity: item.quantity
        })));

        resultsHTML += `
          <div class="seed-calc-product-card">
            <a href="${product.url}" class="seed-calc-product-link">
              <img src="${product.image}" alt="${product.title}" class="seed-calc-product-image">
            </a>
            <div class="seed-calc-product-details">
              <a href="${product.url}" class="seed-calc-product-link">
                <h4 class="seed-calc-product-title">${product.title}</h4>
              </a>
              ${renderProductBadges(product)}
              <div class="seed-calc-product-info-box">
                <div class="seed-calc-lbs-needed">
                  <strong>You need:</strong>
                  <div class="seed-calc-lbs-needed-box">
                    <span class="lbs-amount">${lbsNeeded.toFixed(2)} lbs</span>
                    <span class="sqft-amount">${Math.round(sqft).toLocaleString()} sq ft</span>
                  </div>
                </div>
                <div class="seed-calc-recommended-sizes">
                  <strong>Recommended sizes:</strong>
                  <div class="variant-table">
                    ${variantTableRows}
                  </div>
                </div>
              </div>
              <button class="seed-calc-add-to-cart-btn" data-product-id="${product.id}" data-cart-items='${cartItems}'>
                <span class="add-cart-text">Add to Cart</span>
              </button>
            </div>
          </div>
        `;
      });

      resultsHTML += '</div>';

      console.log('Displayed', displayedProductCount, 'out of', recommendedProducts.length, 'filtered products');
    } else {
      if (selectedTags.length > 0) {
        resultsHTML += `
          <div class="seed-calc-no-results">
            <p class="seed-calc-no-results-title">
              <strong>No products match your criteria</strong>
            </p>
            <p class="seed-calc-no-results-text">
              Try adjusting your answers above or <a href="#" onclick="location.reload(); return false;">start over</a>.
            </p>
            <p class="seed-calc-no-results-hint">
              Selected filters may be too specific for available products in your region.
            </p>
          </div>
        `;
      } else {
        resultsHTML += '<p class="seed-calc-no-products">No products available for your region.</p>';
      }
    }

    document.getElementById('seed-results').innerHTML = resultsHTML;
    document.getElementById('seed-results-modal').classList.add('active');

    // Add event listeners for add to cart buttons
    setTimeout(() => {
      document.querySelectorAll('.seed-calc-add-to-cart-btn').forEach(btn => {
        btn.addEventListener('click', handleProductAddToCart);
      });
    }, 100);
  }

  // Handle add to cart for product recommendations
  function handleProductAddToCart(e) {
    const button = e.currentTarget;
    const cartItemsData = button.dataset.cartItems;

    if (!cartItemsData) return;

    const items = JSON.parse(cartItemsData);
    if (!items || items.length === 0) return;

    button.disabled = true;
    button.querySelector('.add-cart-text').textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(response => {
      console.log('Cart add response status:', response.status);
      if (!response.ok) {
        return response.json().then(err => {
          throw new Error(err.description || err.message || 'Failed to add to cart');
        });
      }
      return response.json();
    })
    .then(data => {
      console.log('Cart add success:', data);
      button.querySelector('.add-cart-text').textContent = 'Added to Cart!';
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Close the results modal
      const resultsModal = document.getElementById('seed-results-modal');
      if (resultsModal) {
        resultsModal.classList.remove('active');
      }

      // Open cart drawer - try Rebuy first, then fall back to theme default
      if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
        window.Rebuy.SmartCart.show();
      } else if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.show === 'function') {
        window.Rebuy.Cart.show();
      } else {
        // Fallback to default Shopify theme cart notification/drawer
        const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (typeof cartDrawer.getSectionsToRender === 'function' && typeof cartDrawer.renderContents === 'function') {
            // Fetch cart with sections to properly update the drawer
            const sections = cartDrawer.getSectionsToRender().map(section => section.id);
            fetch(`${window.location.pathname}?sections=${sections.join(',')}`)
              .then(response => response.json())
              .then(responseData => {
                cartDrawer.renderContents({ sections: responseData });
              })
              .catch(err => {
                console.error('Error fetching cart sections:', err);
                if (typeof cartDrawer.open === 'function') cartDrawer.open();
              });
          } else if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      }

      setTimeout(() => {
        button.disabled = false;
        button.querySelector('.add-cart-text').textContent = 'Add to Cart';
      }, 2000);
    })
    .catch(error => {
      console.error('Cart add error:', error);
      alert('Unable to add to cart: ' + error.message);
      button.disabled = false;
      button.querySelector('.add-cart-text').textContent = 'Add to Cart';
    });
  }

  // Setup calculator button listeners
  function setupCalculatorListeners() {
    // Calculate Requirements button
    const calculateBtn = document.getElementById('seed-calculate-btn');
    if (calculateBtn) {
      calculateBtn.addEventListener('click', () => {
        validateAndCalculate();
      });
    }

    function validateAndCalculate() {
      const zipInput = document.getElementById('seed-zip-input');
      const zipValue = zipInput.value.trim();

      // Validate ZIP code
      if (!zipValue || zipValue.length !== 5) {
        zipInput.style.borderColor = '#d32f2f';
        zipInput.style.backgroundColor = '#ffebee';
        zipInput.focus();
        alert('Please enter a valid 5-digit ZIP code.');
        return;
      }

      // Look up zone from ZIP
      const region = window.zipToClimateRegion && window.zipToClimateRegion[zipValue];

      if (!region) {
        zipInput.style.borderColor = '#ff9800';
        zipInput.style.backgroundColor = '#fff3e0';
        zipInput.focus();
        alert('ZIP code not found. Please check your ZIP code and try again.');
        return;
      }

      // Set the detected region and ZIP
      detectedRegion = region.toLowerCase().trim();
      userZipCode = zipValue;
      zipEntered = true;

      // Look up USDA zone
      const zoneData = getUSDAZone(zipValue);
      if (zoneData) {
        usdaHardinessZone = zoneData.zone;
        usdaTempRange = zoneData.trange;
      }

      // Visual feedback
      zipInput.style.borderColor = '';
      zipInput.style.backgroundColor = '';

      // Check area
      const manualArea = parseFloat(document.getElementById('seed-area-field').value) || 0;
      if (manualArea > 0) {
        updateResultsFromSqft(manualArea);
      } else {
        alert('Please enter your lawn area or use the "Measure area on map" button to draw on the map.');
      }
    }

    // Close results modal handlers
    const closeResultsBtn = document.getElementById('seed-close-modal');
    if (closeResultsBtn) {
      closeResultsBtn.addEventListener('click', () => {
        document.getElementById('seed-results-modal').classList.remove('active');
      });
    }

    const resultsModal = document.getElementById('seed-results-modal');
    if (resultsModal) {
      resultsModal.addEventListener('click', (e) => {
        if (e.target.id === 'seed-results-modal') {
          document.getElementById('seed-results-modal').classList.remove('active');
        }
      });
    }
  }

  // Setup modal button listeners
  function setupModalListeners() {
    // Open map modal button
    const openMapBtn = document.getElementById('seed-open-map-btn');
    if (openMapBtn) {
      openMapBtn.addEventListener('click', function() {
        if (typeof window.openAreaMapModal_seed_map_modal === 'function') {
          window.openAreaMapModal_seed_map_modal();
        }
      });
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupCalculatorListeners();
      setupModalListeners();
    });
  } else {
    setupCalculatorListeners();
    setupModalListeners();
  }
})();
